import AddIcon from "@atlaskit/icon/core/add";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import DownloadIcon from "@atlaskit/icon/core/download";
import ShowMoreVerticalIcon from "@atlaskit/icon/core/show-more-vertical";
import {token} from "@atlaskit/tokens";
import {createFileRoute, useSearch} from "@tanstack/react-router";
import type {ScopeFilterConfig} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDataViewerConfig";
import {useQueryClient} from "@tanstack/react-query";
import {useEffect, useRef, useState} from "react";
import {z} from "zod";
import {BottomDrawer} from "~/components/BottomDrawer";
import {ExportOptionsDrawerContent} from "~/components/dataActions/ExportOptionsDrawerContent";
import {OttopilotDataViewer} from "~/components/ottopilotDataViewer/ottopilotDataViewer";

import {useOttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {useOttopilotExportActions} from "~/components/ottopilotDataViewer/useOttopilotExportActions";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {ViewType} from "~/components/ottopilotDataViewer/view";
import {TableOnlyPage} from "~/components/tableOnlyPage";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {CreateBtlActivityTemplateComponent} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/btl-activity-templates/index/-createComponent";
import {getBtlActivityTemplateLineItems} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/btl-activity-templates/index/-line-items";
import {BtlActivityTemplateViewPanel} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/btl-activity-templates/index/-view-panel";
import {BtlActivityTemplatePreviewPanel} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/btl-activity-templates/index/-previewFromRow";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {SortDirection} from "~/components/ottopilotDataViewer/sortConfiguration";
import {useCheckMobile} from "~/hooks/useCheckMobile";
import {getBtlActivityTemplate_ColumnValues, getBtlActivityTemplates, getBtlActivityTemplates_Length} from "~/utilities/apis/api";
import {WORKSPACE__BTL_TEMPLATES__QUERY_KEY} from "~/utilities/queryKeys";

import {uniqueUuid} from "@vesta/unique/unique";
import {BtlActivityTemplateStatus, type BtlActivityTemplateData} from "~/domainModels/btlActivityTemplates";

const BTL_TEMPLATES_VIEWER_QUERY_KEY_PREFIX = uniqueUuid`e9f0a1b2-c3d4-4e5f-9a0b-1c2d3e4f5a6b`;
const BTL_TEMPLATES_TABLE_VIEW_ID = uniqueUuid`f0a1b2c3-d4e5-4f6a-0b1c-2d3e4f5a6b7c`;

const BTL_TEMPLATES_SCOPE_FILTER_CONFIG: ScopeFilterConfig = {
    drawerTitle: "Type",
    selectionMode: "single",
    options: [
        {
            id: uniqueUuid`7e8f9a0b-1c2d-4e5f-8a9b-0c1d2e3f4b03`,
            type: "User",
            label: "Created by me",
            filterKey: "createdBy",
        },
    ],
};

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/btl-activity-templates/",
)({
    validateSearch: z.object({
        view: z.enum(["table", "create"]).optional().default("table"),
    }),
    component: RouteComponent,
});

function RouteComponent() {
    const search = useSearch({
        from: "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/btl-activity-templates/",
    });
    const isMobile = useCheckMobile();
    const queryClient = useQueryClient();
    const [exportDrawerOpen, setExportDrawerOpen] = useState(false);
    const {workspace} = useWorkspace();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser} = useWorkspaceUser();
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const btlActivityTemplateLineItems = getBtlActivityTemplateLineItems(users, userTemplates);

    const btlActivityTemplatesDataViewerController = useOttopilotDataViewerController<BtlActivityTemplateData>({
        updatePanelOpensInFullscreen: true,
        rowDisplayNameVariants: {
            singularSentenceCase: "BTL template",
            pluralSentenceCase: "BTL templates",
            singularLowerCase: "btl activity template",
            pluralLowerCase: "btl activity templates",
        },
        queryKeyPrefix: [BTL_TEMPLATES_VIEWER_QUERY_KEY_PREFIX],
        hideEditPanelFooter: true,
        scopeFilterConfig: BTL_TEMPLATES_SCOPE_FILTER_CONFIG,
        initialSortConfiguration: {
            revisionId: uniqueUuid`b4c5d6e7-f8a9-4b0c-9d1e-2f3a4b5c6d7e`,
            lineItems: [
                {
                    columnId: "updatedAt",
                    direction: SortDirection.descending,
                },
            ],
        },
        rowsQueryFn: async ({filterConfiguration, limit, offset, sortConfiguration, searchConfiguration}) => {
            // return [];
            const btlActivityTemplatesResult = await getBtlActivityTemplates({
                workspaceId: workspace.id,
                offset: offset,
                limit: limit,
                filterConfiguration: filterConfiguration,
                sortConfiguration: sortConfiguration,
                searchConfiguration: searchConfiguration,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (btlActivityTemplatesResult.success !== true) {
                throw btlActivityTemplatesResult.error instanceof Error ? btlActivityTemplatesResult.error : new Error(String(btlActivityTemplatesResult.error));
            }
            const apiTemplates = btlActivityTemplatesResult.data;
            return apiTemplates;
        },
        rowCountQueryFn: async ({filterConfiguration, searchConfiguration}) => {
            // return 0;
            const btlActivityTemplatesLengthResult = await getBtlActivityTemplates_Length({
                workspaceId: workspace.id,
                filterConfiguration: filterConfiguration,
                searchConfiguration: searchConfiguration,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (btlActivityTemplatesLengthResult.success !== true) {
                throw btlActivityTemplatesLengthResult.error instanceof Error ? btlActivityTemplatesLengthResult.error : new Error(String(btlActivityTemplatesLengthResult.error));
            }
            return btlActivityTemplatesLengthResult.data;
        },
        columnValuesQueryFn: async ({columnId}) => {
            // return [];
            if (columnId === "status") {
                // return [
                //     "ef24d213-4c49-4df8-ae95-5c888eac4f56",
                //     "eff50060-c53c-4ed8-9ad2-a8ec2241d02b",
                //     "57cad67a-a109-4a75-b98f-11e6690f4bb5",
                //     "76b9de30-6976-42e5-bc02-688a648726a1",
                // ];
                return Object.values(BtlActivityTemplateStatus);
            }
            const columnValuesResult = await getBtlActivityTemplate_ColumnValues({
                workspaceId: workspace.id,
                columnId: columnId,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (columnValuesResult.success !== true) {
                throw columnValuesResult.error instanceof Error ? columnValuesResult.error : new Error(String(columnValuesResult.error));
            }
            return columnValuesResult.data;
        },
        columns: {
            revisionId: uniqueUuid`a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d`,
            lineItems: btlActivityTemplateLineItems,
        },
        views: [
            {
                id: BTL_TEMPLATES_TABLE_VIEW_ID,
                viewType: ViewType.table,
                visibleColumns: [
                    "displayName",
                    "description",
                    "status",
                    // "createdBy",
                    "createdAt",
                    // "updatedBy",
                    "updatedAt",
                ],
            },
        ],
        initialViewId: BTL_TEMPLATES_TABLE_VIEW_ID,
        createPanelComponent: () => (
            <CreateBtlActivityTemplateComponent
                workspaceId={workspace.id}
                intellsysBaseUrl={intellsysBaseUrl}
                accessToken={heimdallUser.accessToken}
                onClose={() => {
                    btlActivityTemplatesDataViewerController.setCurrentlyOpenRowEngagementPanel({
                        rowEngagementType: RowEngagementType.none,
                        rowEngagementPanelType: RowEngagementPanelType.none,
                        rowEngagementData: null,
                    });
                }}
                onSuccess={() => {
                    void queryClient.invalidateQueries({
                        queryKey: [WORKSPACE__BTL_TEMPLATES__QUERY_KEY, workspace.id],
                    });
                    void queryClient.invalidateQueries({queryKey: [BTL_TEMPLATES_VIEWER_QUERY_KEY_PREFIX]});
                    void queryClient.removeQueries({queryKey: ["btl-activity-template"]});
                    void queryClient.removeQueries({queryKey: ["btl-activity-template-prefill"]});
                    btlActivityTemplatesDataViewerController.setCurrentlyOpenRowEngagementPanel({
                        rowEngagementType: RowEngagementType.none,
                        rowEngagementPanelType: RowEngagementPanelType.none,
                        rowEngagementData: null,
                    });
                }}
            />
        ),
        readPanelComponent: (row, isExpanded) =>
            isExpanded ? (
                <BtlActivityTemplatePreviewPanel
                    row={row}
                    setCurrentlyOpenRowEngagementPanel={btlActivityTemplatesDataViewerController.setCurrentlyOpenRowEngagementPanel}
                />
            ) : (
                <div
                    className="min-h-0 min-w-0"
                    style={{
                        paddingBottom: token("space.250", "20px"),
                        paddingLeft: token("space.250", "20px"),
                        paddingRight: token("space.250", "20px"),
                        paddingTop: token("space.200", "16px"),
                    }}
                >
                    <BtlActivityTemplateViewPanel
                        row={row}
                        isExpanded={false}
                        embedInSidebar={true}
                    />
                </div>
            ),
        updatePanelComponent: (row) => (
            <CreateBtlActivityTemplateComponent
                initialTemplateId={row.id}
                workspaceId={workspace.id}
                intellsysBaseUrl={intellsysBaseUrl}
                accessToken={heimdallUser.accessToken}
                onClose={() => {
                    btlActivityTemplatesDataViewerController.setCurrentlyOpenRowEngagementPanel({
                        rowEngagementType: RowEngagementType.none,
                        rowEngagementPanelType: RowEngagementPanelType.none,
                        rowEngagementData: null,
                    });
                }}
                onSuccess={() => {
                    void queryClient.invalidateQueries({
                        queryKey: [WORKSPACE__BTL_TEMPLATES__QUERY_KEY, workspace.id],
                    });
                    void queryClient.invalidateQueries({queryKey: [BTL_TEMPLATES_VIEWER_QUERY_KEY_PREFIX]});
                    void queryClient.removeQueries({queryKey: ["btl-activity-template"]});
                    void queryClient.removeQueries({queryKey: ["btl-activity-template-prefill"]});
                    btlActivityTemplatesDataViewerController.setCurrentlyOpenRowEngagementPanel({
                        rowEngagementType: RowEngagementType.none,
                        rowEngagementPanelType: RowEngagementPanelType.none,
                        rowEngagementData: null,
                    });
                }}
            />
        ),
    });
    const handledCreateSearchRef = useRef(false);
    useEffect(() => {
        if (search.view !== "create") {
            handledCreateSearchRef.current = false;
            return;
        }
        if (handledCreateSearchRef.current === true) {
            return;
        }
        handledCreateSearchRef.current = true;
        btlActivityTemplatesDataViewerController.setCurrentlyOpenRowEngagementPanel({
            rowEngagementType: RowEngagementType.create,
            rowEngagementPanelType: RowEngagementPanelType.fullscreen,
            rowEngagementData: null,
        });
    }, [search.view, btlActivityTemplatesDataViewerController]);

    const {
        onExportPng: handleExportAllRowsAsPng,
        onExportPdf: handleExportAllRowsAsPdf,
    } = useOttopilotExportActions({
        controller: btlActivityTemplatesDataViewerController,

        pngFilename: "btl-activity-templates.png",
        pdfFilename: "btl-activity-templates.pdf",
        documentTitle: "BTL templates",
        csvExportMode: {kind: "disabled"},
        onError: () => undefined,
    });

    return (
        <>
            {/*
             * TableOnlyPage provides AsidePanelProvider + #aside-panel so desktop create fullscreen
             * (AsideFullscreenPanel portal) can render — same shell as beat planning / event template tables.
             */}
            <TableOnlyPage
                title="BTL templates"
                titleRightSection={
                    <div className="grid grid-flow-col auto-cols-max gap-ds-space-100 justify-self-end">
                        {!isMobile ? (
                            <AnankeButton
                                type="button"
                                appearance="primary"
                                iconBefore={(iconProps) => (
                                    <AddIcon
                                        {...iconProps}
                                        label=""
                                    />
                                )}
                                onClick={() => {
                                    btlActivityTemplatesDataViewerController.setCurrentlyOpenRowEngagementPanel({
                                        rowEngagementType: RowEngagementType.create,
                                        rowEngagementPanelType: RowEngagementPanelType.fullscreen,
                                        rowEngagementData: null,
                                    });
                                }}
                            >
                                Create BTL template
                            </AnankeButton>
                        ) : (
                            <button
                                type="button"
                                onClick={() => {
                                    btlActivityTemplatesDataViewerController.setCurrentlyOpenRowEngagementPanel({
                                        rowEngagementType: RowEngagementType.create,
                                        rowEngagementPanelType: RowEngagementPanelType.fullscreen,
                                        rowEngagementData: null,
                                    });
                                }}
                                className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px]"
                                style={{
                                    backgroundColor: token("color.background.brand.bold", "#0052CC"),
                                    color: token("color.icon.inverse", "#FFFFFF"),
                                }}
                                aria-label="Create BTL template"
                            >
                                <AddIcon
                                    label=""
                                    color={token("color.icon.inverse")}
                                />
                            </button>
                        )}
                        {isMobile ? (
                            <button
                                type="button"
                                onClick={() => setExportDrawerOpen(true)}
                                className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"
                                style={{
                                    borderColor: token("color.border", "#091E4224"),
                                    backgroundColor: token("elevation.surface", "#FFFFFF"),
                                    color: token("color.icon", "#44546F"),
                                }}
                            >
                                <ShowMoreVerticalIcon label="Export options" />
                            </button>
                        ) : (
                            <AnankeDropdownMenu<HTMLButtonElement>
                                placement="bottom-start"
                                trigger={({triggerRef, ...triggerProps}) => (
                                    <button
                                        {...triggerProps}
                                        ref={triggerRef}
                                        type="button"
                                        className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"
                                        style={{
                                            borderColor: token("color.border", "#091E4224"),
                                            backgroundColor: token("elevation.surface", "#FFFFFF"),
                                            color: token("color.icon", "#44546F"),
                                        }}
                                    >
                                        <ShowMoreVerticalIcon label="Export options" />
                                    </button>
                                )}
                            >
                                <AnankeDropdownItemGroup>
                                    {/* <AnankeDropdownItem
                                        onClick={() => handleExportAllRowsAsPng()}
                                        elemBefore={<DownloadIcon label="" color={token("color.icon")} />}
                                        elemAfter={<ChevronRightIcon label="" size="small" color={token("color.icon.subtle")} />}
                                    >
                                        Export as PNG
                                    </AnankeDropdownItem> */}
                                    <AnankeDropdownItem
                                        onClick={() => handleExportAllRowsAsPdf()}
                                        elemBefore={
                                            <DownloadIcon
                                                label=""
                                                color={token("color.icon")}
                                            />
                                        }
                                        elemAfter={
                                            <ChevronRightIcon
                                                label=""
                                                size="small"
                                                color={token("color.icon.subtle")}
                                            />
                                        }
                                    >
                                        Export as PDF
                                    </AnankeDropdownItem>
                                </AnankeDropdownItemGroup>
                            </AnankeDropdownMenu>
                        )}
                    </div>
                }
                bottomSection={<OttopilotDataViewer controller={btlActivityTemplatesDataViewerController} />}
            />
            {isMobile ? (
                <BottomDrawer
                    open={exportDrawerOpen}
                    setOpen={setExportDrawerOpen}
                    title="Export"
                    fitContentOnOpen={true}
                >
                    <ExportOptionsDrawerContent
                        onExportAsPng={() => {
                            handleExportAllRowsAsPng();
                            setExportDrawerOpen(false);
                        }}
                        onExportAsPdf={() => {
                            handleExportAllRowsAsPdf();
                            setExportDrawerOpen(false);
                        }}
                    />
                </BottomDrawer>
            ) : null}
        </>
    );
}
